import React, { FC, JSX, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import * as ReactMapGL from "react-map-gl/mapbox";
import { MappingContext } from "@app/mapping/MappingContext";
import { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { FilterType, ProviderActionStatusEnum } from '@app/projects/enumerators';
import FlyoutContextProvider from '@app/shared/Flyouts/FlyoutContext';
import { CountyProjectContext } from '@app/projects/CountyProjectContext';
import { RecenterControl } from '@app/shared/mapping/RecenterControl';
import {
  NetworkCoverageMapDataContext,
  NetworkCoverageMapDataContextProvider
} from '@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext';
import NetworkCoverageMapProviderPopup from '@app/projects/network-assessment/maps/network-coverage-map/components/NetworkCoverageMapProviderPopup';
import MapDetailsBox from '@app/projects/network-assessment/maps/network-coverage-map/MapDetailsBox';
import MapExport from '@app/projects/network-assessment/maps/network-coverage-map/MapExport';
import { getMembershipTypeLabel } from '@app/projects/membershipTypeLabel';
import { MapboxSupportAlert } from '@app/shared/mapping/MapboxSupportAlert';
import {
  MARKET_PROVIDER_COLOR,
  MEMBERS_WITH_ACCESS_COLOR,
  MEMBERS_WITHOUT_ACCESS_COLOR,
  NETWORK_PROVIDER_COLOR,
  PROSPECT_PROVIDER_COLOR,
  TARGET_PROVIDER_COLOR,
  PROVIDER_ICON_SHADOW_COLOR,
  ZIP_CODE_BORDER_COLOR,
  MEMBERS_IMAGE_ID,
  PROVIDERS_SOURCE_ID,
  COUNTY_BORDER_SOURCE_ID,
  COUNTY_BORDER_LAYER_ID,
  MEMBER_SOURCE_ID,
  MEMBER_LAYER_ID,
  SELECTED_PROVIDER_SOURCE_ID,
  PROVIDER_HIGHLIGHTED_LAYERD_ID,
  PROVIDERS_LAYER_ID,
  VIEW_PADDING,
  ACCORDION_OPEN_PADDING,
  ZIPCODE_SOURCE_ID,
  ZIPCODE_LAYER_ID,
  ZIPCODE_LABEL_LAYER_ID,
  ZIPCODE_MIN_ZOOM_LEVEL,
  ZERO_PERCENT_FILTER,
  HUNDRED_PERCENT_FILTER,
  PROVIDER_ICON_UNSELECTED_MIN_SIZE,
  PROVIDER_ICON_SELECTED_SIZE,
  PROVIDER_ICON_UNSELECTED_MAX_SIZE,
  PROVIDER_ICON_IMAGE_ID, EXCLUDED_PROVIDER_COLOR, TERMINATED_PROVIDER_COLOR,
  CONTEXT_COUNTY_COLOR,
  CONTEXT_COUNTY_BORDER_SOURCE_ID,
  CONTEXT_COUNTY_BORDER_LAYER_ID,
  STATE_BORDER_COLOR,
  STATE_BORDER_SOURCE_ID,
  STATE_BORDER_LAYER_ID,
  COUNTY_LABEL_SOURCE_ID,
  COUNTY_LABEL_LAYER_ID,
  COUNTY_LABEL_COLOR,
  COUNTY_LABEL_TEXT_SIZE,
  COUNTY_LABEL_TEXT_OPACITY,
  COUNTY_LABEL_HALO_COLOR
} from '@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants';
import { FilterContext, FilterContextProvider } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext';
import { buildMultiMapExportLaunchContext } from '@app/projects/network-assessment/maps/network-coverage-map/utils/buildMultiMapExportLaunchContext';
import FilterControl from '@app/projects/network-assessment/maps/network-coverage-map/controls/FilterControl';
import NetworkCoverageMapLayerControl from '@app/projects/network-assessment/maps/network-coverage-map/controls/NetworkCoverageMapLayerControl';
import NetworkCoverageMapLegendControl from '@app/projects/network-assessment/maps/network-coverage-map/controls/NetworkCoverageMapLegendControl';
import { ProviderAccordionControl } from '@app/projects/network-assessment/maps/network-coverage-map/controls/ProviderAccordionControl';
import { EasingOptions } from 'mapbox-gl';
import { NetworkCoverageMapProps } from '@app/projects/network-assessment/maps/network-coverage-map/interfaces';
import { MapPropertyKeysEnum } from '@app/projects/network-assessment/maps/mapPropertyKeysEnum';
import { EXCLUDE_ZIP_POINTS_FILTER, INCLUDE_ZIP_POINTS_FILTER } from '@app/shared/mapping/zipPoints';
import { ProviderNetworkTypeEnum } from '@app/shared/enums/providerNetworkTypeEnum';
import { UserContext } from '@app/user/UserContext';
import Roles from '@app/user/roles';
import { FeatureFlagContext } from '@app/feature-flags/FeatureFlagContext';
import Features from '@app/feature-flags/features';
import { countyFilterPathRegEx } from '@app/projects/helpers';

const WrappedNetworkCoverageMap: FC<React.PropsWithChildren<NetworkCoverageMapProps>> = (props: NetworkCoverageMapProps): JSX.Element => {
  const history = useHistory();

  // Contexts
  const filterContext = useContext(FilterContext);
  const mappingContext = useContext(MappingContext);
  const projectContext = useContext(CountyProjectContext);
  const mapDataContext = useContext(NetworkCoverageMapDataContext);
  const userContext = useContext(UserContext);
  const featureFlagContext = useContext(FeatureFlagContext);

  const multiMapExportEnabled = featureFlagContext.enabledFeatureFlags.has(Features.VOLUME_MAP_EXPORTS_COUNTY);
  const serviceAreaWideMapping = featureFlagContext.enabledFeatureFlags.has(Features.SERVICE_AREA_WIDE_MAPPING_COUNTY);
  const multiMapExportRunConfigurationId = filterContext.selectedTimeslice ?? projectContext.runConfiguration?.projectRunConfigurationId;

  if (!projectContext.runConfiguration) {
    return (
      <div className='skeleton w-full shimmer' style={{ height: '400px' }}>
        <div className='bone w-full h-100' />
      </div>
    );
  }

  // Images constants
  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const images = [
    { id: MEMBERS_IMAGE_ID, path: `${imagesDir}Members.png` },
  ];

  const [viewState, setViewState] = useState<ReactMapGL.ViewState>(mappingContext.defaultViewState);

  const isProjectActionsVisible  = projectContext.runConfiguration?.isLatestPublished  || !projectContext.runConfiguration?.datePublished;

  const clearAllFilters = () => {
    projectContext.filters.forEach((filter) => {
      if (filter.urlPart && filter.filterType !== FilterType.County) {
        history.location.pathname = history.location.pathname.replace(filter.urlPart, "");
      }
    });

    if(projectContext.filters.some(f => f.filterType === FilterType.County))
    {
      const pathname = history.location.pathname;
      history.location.pathname = history.location.pathname.replace(pathname.match(countyFilterPathRegEx)?.[0], "");
    }

    history.push(history.location.pathname);
    projectContext.clearFilters();
    filterContext.setSelectedProviderSources([ProviderNetworkTypeEnum.Network]);
    // Reset county statuses using the context's default logic
    if (filterContext.getDefaultSelectedStatuses && filterContext.availableCountyStatuses && filterContext.setSelectedCountyStatuses) {
      const defaultStatuses = filterContext.getDefaultSelectedStatuses(filterContext.availableCountyStatuses);
      filterContext.setSelectedCountyStatuses(defaultStatuses);
    }
    filterContext.setSelectedServiceAreas([]);
    filterContext.setSelectedServiceArea('');
    mapDataContext.setFilterGainsMinPercent(ZERO_PERCENT_FILTER);
    mapDataContext.setFilterGainsMaxPercent(HUNDRED_PERCENT_FILTER);
    mapDataContext.setFilterImpactsMinPercent(ZERO_PERCENT_FILTER);
    mapDataContext.setFilterImpactsMaxPercent(HUNDRED_PERCENT_FILTER);
    mapDataContext.setFilterServesMinPercent(ZERO_PERCENT_FILTER);
    mapDataContext.setFilterServesMaxPercent(HUNDRED_PERCENT_FILTER);
    mapDataContext.setSelectedLineOfBusiness(null);
    mapDataContext.setSelectedVolumeIndicators([]);
    mapDataContext.setSelectedEffectiveness([]);
    mapDataContext.setSelectedEfficiency([]);
  };

  const updateMapInputs = () => {
    if (filterContext.isFilterActive) {
      filterContext.setIsFilterActive(false);
    }

    mapDataContext.resetMapData();
  }

  const recenterMap = useCallback((): void => {
    setViewState(mappingContext.defaultViewState);
    mapDataContext.setMinZoomLevel(mappingContext.defaultZoom - .5);
  }, [viewState]);

  const createMap = useCallback(async (): Promise<void> => {
    const map = mapDataContext.mapRef.current;

    if (!map) return;
    mapDataContext.applyMemberLayerDefaults();
    filterContext.setIsFilterActive(true);
  }, [
    filterContext.selectedServiceArea,
    filterContext.selectedServiceAreas,
    filterContext.selectedSpecialtyGroupCode.key,
    mapDataContext.members,
    mapDataContext.providers,
  ]);

  const onMapLoad = useCallback((): void => {
    const map = mapDataContext.mapRef.current?.getMap();

    if (!map) return;

    const handleMapStyleChange = () => {
      // When changing the styles, even though the Source and Layers are preserved,
      // we need to re-add the images, since they are tied to the style object
      loadImagesToMap(map);
    }

    map.on('styledata', handleMapStyleChange);
    loadImagesToMap(map);// Initial load of images
    mapDataContext.mapRef.current.setCenter([mappingContext.defaultLongitude, mappingContext.defaultLatitude]);
  }, []);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: mappingContext.defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: mappingContext.defaultPitch
    });
  }, []);

  const loadImagesToMap = (map: ReactMapGL.MapInstance): void => {
    images.forEach(({ id, path }) => {
      if (!map.hasImage(id)) {
        map.loadImage(path, (error: Error | null, image: HTMLImageElement | undefined) => {
          if (error || !image) return;

          if (!map.hasImage(id)) {
            map.addImage(id, image, { sdf: true });
          }
        });
      }
    });
  }

  const handleMapMove = useCallback(() => {
    const newBounds = mapDataContext.mapRef.current?.getMap().getBounds();
    mapDataContext.setCurrentMapBoundary(newBounds);
  }, []);

  const easingOptions = (): EasingOptions => {
    return {
      duration: 1000,
      padding: {
        bottom: VIEW_PADDING,
        left: mapDataContext.isProviderAccordionOpen ? ACCORDION_OPEN_PADDING : VIEW_PADDING,
        right: VIEW_PADDING,
        top: VIEW_PADDING
      },
      retainPadding: false
    };
  }

  const handleRecenterIconClick = () => {
    if (mapDataContext.dataLoaded && !!mapDataContext.dataBoundingBox) {
      mapDataContext.mapRef.current?.fitBounds(mapDataContext.dataBoundingBox, easingOptions());
    } else {
      recenterMap();
    }
  }

  const handleProcessTargetProviderChanges = async (reprocessFailure: boolean): Promise<void> => {
    props.processProviderChanges(reprocessFailure);
    mapDataContext.triggerDataRefresh();
  };

  const handlePojectHasUnprocessedProjectionProviders = (has: boolean): void => {
    props.setProjectHasUnprocessedProjectionProviders(has);
    mapDataContext.triggerDataRefresh();
  };

  useEffect(() => {
    handleRecenterIconClick();
  }, [mapDataContext.dataBoundingBox, mapDataContext.dataLoaded]);

  useEffect(() => {
    if (!!mapDataContext.selectedProvider) {
      const selectedProviderCard = mapDataContext.selectedProvider;

      mapDataContext.mapRef.current?.easeTo({
        center: [selectedProviderCard.longitude, selectedProviderCard.latitude],
        duration: 1000
      });
    }
  }, [mapDataContext.selectedProvider]);

  return (
    !!projectContext.runConfiguration &&
    <div>
      <div className="network-coverage-map__export-button-container">
        {mapDataContext.currentAdequacySummarySpecialtyGroup
          ? <MapExport
            isLoading={mapDataContext.loadingProviders}
            launchContext={buildMultiMapExportLaunchContext(filterContext)}
            membershipType={getMembershipTypeLabel(projectContext.runConfiguration)}
            projectId={projectContext.project.projectId}
            projectRunConfigurationId={filterContext.selectedTimeslice}
            runConfigurationDateCreated={projectContext.runConfiguration.dateCreated}
            projectName={projectContext.project.resource.name}
            specialtyDetailInfo={mapDataContext.currentAdequacySummarySpecialtyGroup.footnote}
          />
          : multiMapExportEnabled &&
          <Link
            className="qes-button qes-button-primary"
            data-testid="network-coverage-map__create-multiple-maps-button"
            to={`/projects/${projectContext.project.projectId}/${multiMapExportRunConfigurationId}/map-exporter`}
          >
            Create Multiple Maps
          </Link>
        }
      </div>

      <div className="network-coverage-map__map-container">
        <MapDetailsBox
          adequacySummarySpecialtyGroup={mapDataContext.currentAdequacySummarySpecialtyGroup}
          cmsCreditIsApplying={projectContext.cmsCreditIsApplying}
          project={projectContext.project}
          runConfiguration={projectContext.runConfiguration}
        />

        <div className="network-coverage-map__map">
          <ReactMapGL.Map
            {...viewState}
            {...mappingContext.defaultMapProps}
            mapStyle={mapDataContext.mapStyle}
            onClick={(e) => mapDataContext.handleProviderIconClick(e)}
            interactiveLayerIds={[PROVIDERS_LAYER_ID]}
            onLoad={onMapLoad}
            onMove={(e) => onMapMove(e.viewState)}
            onMoveEnd={handleMapMove}
            preserveDrawingBuffer={true}
            ref={mapDataContext.mapRef}
          >
            {/* The Mapbox Expression logic docs: https://docs.mapbox.com/style-spec/reference/expressions/ */}

            {/* Context County Borders Layer: unselected counties in the selected and neighboring states */}

            {serviceAreaWideMapping &&
              <Source id={CONTEXT_COUNTY_BORDER_SOURCE_ID} type="geojson" data={mapDataContext.countyContextData?.boundaries ?? { features: [], type: "FeatureCollection" }}>
                <Layer
                  id={CONTEXT_COUNTY_BORDER_LAYER_ID}
                  type="line"
                  paint={{
                    "line-color": CONTEXT_COUNTY_COLOR,
                    "line-width": 1,
                  }}
                />
              </Source>
            }

            {/* State Borders Layer: the selected and neighboring states, beneath the selected county lines */}

            {serviceAreaWideMapping &&
              <Source id={STATE_BORDER_SOURCE_ID} type="geojson" data={mapDataContext.countyContextData?.states ?? { features: [], type: "FeatureCollection" }}>
                <Layer
                  id={STATE_BORDER_LAYER_ID}
                  type="line"
                  paint={{
                    "line-color": STATE_BORDER_COLOR,
                    "line-width": 2,
                  }}
                />
              </Source>
            }

            {/* County Borders Layer */}

            <Source id={COUNTY_BORDER_SOURCE_ID} type="geojson" data={mapDataContext.countyBorderData ?? { features: [], type: "FeatureCollection" }}>
              <Layer
                id={COUNTY_BORDER_LAYER_ID}
                type="line"
                paint={{
                  "line-color": MARKET_PROVIDER_COLOR,
                  "line-width": 1.5,
                }}
              />
            </Source>
            {/* Members Layer */}

            <Source
              id={MEMBER_SOURCE_ID}
              type="geojson"
              minzoom={mapDataContext.minZoomLevel}
              data={mapDataContext.memberFeatures}
            >
              <Layer
                id={MEMBER_LAYER_ID}
                type="symbol"
                layout={{
                  "icon-allow-overlap": true,
                  "icon-image": MEMBERS_IMAGE_ID,
                  "icon-size": 0.2
                }}
                paint={{
                  'icon-color': [
                    'case',
                    // Get the 'covered' property from the member data where it's true
                    ['==', ['get', MapPropertyKeysEnum.Covered], true],
                    // If member has access
                    MEMBERS_WITH_ACCESS_COLOR,
                    // If member does not have access
                    MEMBERS_WITHOUT_ACCESS_COLOR
                  ]
                }}
                filter={[
                  'case',
                  // Check if the member is covered or not
                  // If the member is covered, show them as with access
                  ['==', ['get', MapPropertyKeysEnum.Covered], true],
                  mapDataContext.showMembersWithAccess,
                  // If the member is not covered, show them as without access
                  ['==', ['get', MapPropertyKeysEnum.Covered], false],
                  mapDataContext.showMembersWithoutAccess,
                  // Default case
                  // If neither are selected, don't show any members
                  false
                ]}
              />
            </Source>

            {/* Selected Provider shadow layer */}

            <Source
              id={SELECTED_PROVIDER_SOURCE_ID}
              type="geojson"
              data={mapDataContext.selectedProviderFeature}
            >
              <Layer
                id={PROVIDER_HIGHLIGHTED_LAYERD_ID}
                type="circle"
                paint={{
                  'circle-blur': 1,
                  'circle-color': PROVIDER_ICON_SHADOW_COLOR,
                  'circle-radius': 40,
                }}
              />
            </Source>

            {/* Providers Layer */}

            <Source
              id={PROVIDERS_SOURCE_ID}
              type="geojson"
              minzoom={mapDataContext.minZoomLevel}
              data={mapDataContext.providerFeatures}
            >
              <Layer
                id={PROVIDERS_LAYER_ID}
                type="symbol"
                layout={{
                  "icon-allow-overlap": true,
                  'icon-image': [
                    'image',
                    PROVIDER_ICON_IMAGE_ID,
                    {params: {
                      icon_center_border: "black",
                      icon_center_color: "white",
                      icon_primary_border: "black",
                      icon_primary_color: [
                      // Use a case statement to determine the color based on the provider type
                      'case',
                      // Only consider the provider action color if the map is being viewed from the latest published or unpublished timeslice.
                      ...(isProjectActionsVisible ?
                        [
                          // If the provider has a provider action, use the provider action color.
                          // Provider action goes first to ensure it takes precedence over the network types.
                          ['==', ['get', MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTargeted],
                          TARGET_PROVIDER_COLOR,
                          ['==', ['get', MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyExcluded],
                          EXCLUDED_PROVIDER_COLOR,
                          ['==', ['get', MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTerminated],
                          userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) || userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER) ?
                            TERMINATED_PROVIDER_COLOR :
                            NETWORK_PROVIDER_COLOR,
                        ] : []),
                      ['==', ['get', MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Market],
                      MARKET_PROVIDER_COLOR,
                      ['==', ['get', MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Prospect],
                      PROSPECT_PROVIDER_COLOR,
                      // Default
                      NETWORK_PROVIDER_COLOR
                    ]}}
                  ],
                  'icon-size': [
                    'interpolate',
                    // Linearly scale the size between zoom level 10 and 20
                    ['linear'],
                    ['zoom'],
                    // Zoom is less than or equal to 10 the size is min / selected
                    10,
                    [
                      'case',
                      ['==', ['get', MapPropertyKeysEnum.ProviderUniqueId], mapDataContext.selectedProvider?.providerUniqueId ?? null],
                      PROVIDER_ICON_SELECTED_SIZE,
                      PROVIDER_ICON_UNSELECTED_MIN_SIZE
                    ],
                    // Zoom is greater than or equal to 20 the size is max / selected
                    20,
                    [
                      'case',
                      ['==', ['get', MapPropertyKeysEnum.ProviderUniqueId], mapDataContext.selectedProvider?.providerUniqueId ?? null],
                      PROVIDER_ICON_SELECTED_SIZE,
                      PROVIDER_ICON_UNSELECTED_MAX_SIZE,
                    ]
                  ],
                  'symbol-sort-key': [
                    'case',
                    // Check if the provider is selected, if so, give it the highest sort key
                    ['==', ['get', MapPropertyKeysEnum.ProviderUniqueId], mapDataContext.selectedProvider?.providerUniqueId ?? null],
                    100,
                    // Only show the target icon if the map is being viewed from the latest published or unpublished timeslice
                    ...(isProjectActionsVisible ?
                      [
                        // If the provider has a provider action, it gets the higher priority icon
                        // to be able to view / see that there are other providers at the location.
                        ['==', ['get', MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTargeted],
                        60,
                        ['==', ['get', MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyExcluded],
                        50,
                        ['==', ['get', MapPropertyKeysEnum.ScenarioModelProviderStatus], ProviderActionStatusEnum.FullyTerminated],
                        40,
                      ] : []),
                    // If the provider is prospect, give it the next highest sort key
                    ['==', ['get', MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Prospect],
                    30,
                    // If the provider is market, give it the next highest sort key
                    ['==', ['get', MapPropertyKeysEnum.ProviderNetworkType], ProviderNetworkTypeEnum.Market],
                    20,
                    // Default sort key for network providers
                    10
                  ]
                }}
              />
            </Source>

            {/* Zip Code Layer */}
            <Source
              id={ZIPCODE_SOURCE_ID}
              type="geojson"
              data={mapDataContext.zipCodeData}>
              <Layer
                id={ZIPCODE_LAYER_ID}
                type="line"
                minzoom={ZIPCODE_MIN_ZOOM_LEVEL}
                layout={{
                  "visibility": mapDataContext.showZipCodes ? "visible" : "none"
                }}
                paint={{
                  "line-color": ZIP_CODE_BORDER_COLOR,
                  "line-dasharray": [2, 2],
                  "line-width": 1.5
                }}
              />
              <Layer
                id={ZIPCODE_LABEL_LAYER_ID}
                type="symbol"
                minzoom={ZIPCODE_MIN_ZOOM_LEVEL}
                filter={mapDataContext.showZipPoints ? INCLUDE_ZIP_POINTS_FILTER : EXCLUDE_ZIP_POINTS_FILTER}
                layout={{
                  "text-allow-overlap": true,
                  "text-anchor": "center",
                  "text-field": ["get", MapPropertyKeysEnum.ZipCode],
                  "text-size": 14,
                  "visibility": mapDataContext.showZipCodes ? "visible" : "none"
                }}
                paint={{
                  "text-color": "#2D2D2D",
                  "text-halo-color": "#fff",
                  "text-halo-width": 1
                }}
              />
            </Source>

            {/* County Labels Layer: above the pins so pins never hide them */}

            {serviceAreaWideMapping &&
              <Source id={COUNTY_LABEL_SOURCE_ID} type="geojson" data={mapDataContext.countyContextData?.labels ?? { features: [], type: "FeatureCollection" }}>
                <Layer
                  id={COUNTY_LABEL_LAYER_ID}
                  type="symbol"
                  layout={{
                    // Selected counties are placed first, so their labels win when labels overlap.
                    "symbol-sort-key": ["case", ["get", MapPropertyKeysEnum.Selected], 0, 1],
                    "text-anchor": "center",
                    "text-field": ["get", MapPropertyKeysEnum.County],
                    "text-size": COUNTY_LABEL_TEXT_SIZE,
                  }}
                  paint={{
                    "text-color": COUNTY_LABEL_COLOR,
                    "text-halo-color": COUNTY_LABEL_HALO_COLOR,
                    "text-halo-width": 1,
                    "text-opacity": COUNTY_LABEL_TEXT_OPACITY,
                  }}
                />
              </Source>
            }

            {/* Popup displayed above the highlighted icon */}

            {!!mapDataContext.selectedProvider &&
              mapDataContext.isProviderPopupOpen &&
              <NetworkCoverageMapProviderPopup
                provider={mapDataContext.selectedProvider}
                processProviderChanges={handleProcessTargetProviderChanges}
                setProjectHasUnprocessedProjectionProviders={handlePojectHasUnprocessedProjectionProviders}
                onClose={mapDataContext.handleProviderPopupClose}
              />
            }

            {/* Zoom Control */}
            <NavigationControl data-html2canvas-ignore="true" position="top-right" showCompass={false} />

            <RecenterControl
              ignoreInMapExport={true}
              imagePath={`${imagesDir}Recenter.png`}
              mapRef={mapDataContext.mapRef}
              onClick={handleRecenterIconClick}
              position="top-right"
            />

            {filterContext.isFilterActive &&
              <NetworkCoverageMapLayerControl
                ignoreInMapExport={true}
                imagePath={`${imagesDir}Layer_Hover.png`}
                position="top-right"
              />
            }

            {filterContext.isFilterActive &&
              <NetworkCoverageMapLegendControl position="bottom-right" />
            }

            {!filterContext.isFilterActive &&
              <FilterControl
                ignoreInMapExport={true}
                clearAllFilters={clearAllFilters}
                createMap={createMap}
                position="top-left"
              />
            }

            {filterContext.isFilterActive &&
              <ProviderAccordionControl updateMapInputs={updateMapInputs} ignoreInMapExport={true} position="top-left" />
            }
          </ReactMapGL.Map>
        </div>
      </div>
    </div>
  );
};

export const NetworkCoverageMap: FC<React.PropsWithChildren<NetworkCoverageMapProps>> = (props: NetworkCoverageMapProps) => (
  <FilterContextProvider>
    <NetworkCoverageMapDataContextProvider>
      <FlyoutContextProvider>
        <MapboxSupportAlert>
          <WrappedNetworkCoverageMap {...props} />
        </MapboxSupportAlert>
      </FlyoutContextProvider>
    </NetworkCoverageMapDataContextProvider>
  </FilterContextProvider>
);